import { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { View, StyleSheet, Keyboard, TouchableWithoutFeedback } from 'react-native';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import getSymbolFromCurrency from 'currency-symbol-map';
import { syncSingleAccountConnection, getSyncingStatus } from 'actions/connections';
import Spacer from 'design-system/Spacer';
import Text, { styles } from 'design-system/Text';
import { IconBoldArrow } from 'design-system/icons';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import { rem, AppView, CardView, withAnimatedNavigationHeader } from 'design-system';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { getBalance } from 'utils/api/getBalance';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { formatAmount } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
import { MoneyTextInput } from 'utils/packages/moneyInput';
import { selectDefaultCurrency } from 'reducers/selectors';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { muteAllButThrows, useFetchOld } from 'hooks/useFetch';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { parseMoneyInputText } from 'hooks/useMoneyInputText';
import { selectTradingAccountFromId } from 'features/invest/reducers/selectors';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { isAndroid, isWeb } from '../../../constants';
import SecclAccountRow from '../components/SecclAccountRow';
import FundingAccountRow from '../components/FundingAccountRow';
import ConnectedAccountOrBankAccountRow from '../components/ConnectedAccountOrBankAccountRow';
import createTopUpErrorHandler from '../utils/createTopUpErrorHandler';
import { selectConnectedAccountBalanceFromConnectedAccountWithBankId, selectConnectedAccountWithBankId, } from '../hooks/selectors';
import { getDestination } from '../utils/format';
const extraHeightWihOnlyAmount = isAndroid ? rem(156) : rem(220);
const strings = {
    minAmountErrorPrefix: 'Minimum amount is ',
    exceedBalanceError: 'Your balance is too low',
    leftToInvest: ' left to invest this tax year',
};
const DepositToSecclUsingBankOrConnectedAccount = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const scrollValue = useSharedValue(0);
    const textInputRef = useRef(null);
    const [, , , fetch] = useFetchOld(muteAllButThrows);
    const { connectedAccount } = useAppSelector(selectConnectedAccountWithBankId);
    const defaultCurrency = useAppSelector(selectDefaultCurrency);
    const tradingAccount = useAppSelector((state) => selectTradingAccountFromId(state, route.params.tradingAccountId));
    const remainingAllowance = 
    // eslint-disable-next-line no-nested-ternary
    tradingAccount?.accountType === 'JISA'
        ? tradingAccount?.jisaRemainingAllowanceAfterPending
        : tradingAccount?.accountType === 'ISA'
            ? tradingAccount?.isaRemainingAllowanceAfterPending
            : null;
    const dispatch = useAppDispatch();
    const [from, setFrom] = useState(connectedAccount);
    const syncedAccountIds = useRef(new Set());
    const [to] = useState(tradingAccount);
    const [amountError, setAmountError] = useState();
    const connectedAccountBalance = useAppSelector(selectConnectedAccountBalanceFromConnectedAccountWithBankId);
    const fromBalance = useMemo(() => {
        if (from) {
            if ('ukAccountNumber' in from) {
                return connectedAccountBalance;
            }
            const [balance] = getBalance(from);
            return balance || undefined;
        }
        return undefined;
    }, [connectedAccountBalance, from]);
    useEffect(() => {
        if (!to) {
            navigation.goBack();
        }
    }, [to]);
    useEffect(() => {
        if (from && !('ukAccountNumber' in from) && from.id && !syncedAccountIds.current.has(from.id)) {
            syncedAccountIds.current.add(from.id);
            const syncAccounts = async () => {
                try {
                    await fetch(syncSingleAccountConnection(from.id, true));
                    dispatch(getSyncingStatus());
                }
                catch {
                    // handled automatically
                }
            };
            syncAccounts();
        }
    }, [from]);
    const [amountText, setAmountText] = useState('');
    const amount = useMemo(() => parseMoneyInputText(amountText), [amountText]);
    const colors = useColors();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.elements.primary,
            headerRight: () => <HelpScreenButton />,
        });
    }, [colors.elements.primary]);
    useAutoFocusInput(from && to ? textInputRef : undefined);
    const onScroll = useAnimatedScrollHandler((handle) => {
        scrollValue.value = handle.contentOffset.y;
    }, []);
    const From = useMemo(() => {
        if (from) {
            return (<ConnectedAccountOrBankAccountRow account={from} setFrom={setFrom} onScroll={onScroll} scrollValue={scrollValue}/>);
        }
        return <FundingAccountRow setFrom={setFrom} scrollValue={scrollValue} onScroll={onScroll}/>;
    }, [from, onScroll, scrollValue]);
    const To = useMemo(() => {
        if (to?.id) {
            return <SecclAccountRow tradingAccountId={to.id}/>;
        }
        return null;
    }, [to?.id]);
    const handleContinue = useCallback(() => {
        Keyboard.dismiss();
        const numberAmount = amount ?? 0;
        if (from && to && numberAmount) {
            navigation.navigate('InvestCreateTopUp', {
                flow: 'topupSecclAccount',
                destination: getDestination(tradingAccount?.accountType),
                bankAccount: from,
                amount: numberAmount,
                backCount: 3,
                onError: createTopUpErrorHandler(handleContinue),
                reference: 'EMMA TRADING',
                tradingAccountId: route.params.tradingAccountId,
            });
        }
    }, [amount, from, route.params.tradingAccountId, to, tradingAccount?.accountType]);
    const checkAmountError = useCallback((value) => {
        if (value < 1) {
            setAmountError('belowMin');
        }
        else if (remainingAllowance != null && value > remainingAllowance.value) {
            setAmountError('exceedAllowance');
        }
        else if (fromBalance && value > fromBalance.amount) {
            setAmountError('exceedBalance');
        }
        else {
            setAmountError(undefined);
        }
    }, [remainingAllowance, fromBalance]);
    useEffect(() => {
        if (amount) {
            checkAmountError(amount);
        }
        else {
            setAmountError(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkAmountError]); // we want this hook to run only if checkAmountError updates
    const footnote = useMemo(() => {
        const isError = !!amountError;
        let text = '';
        if (remainingAllowance) {
            text = (<>
              <Text NumbersThin-14 {...(isError ? { Red: true } : { Secondary: true })}>
                {formatAmount(remainingAllowance.value, remainingAllowance.currency, 2)}
              </Text>
              {strings.leftToInvest}
            </>);
        }
        if (amountError === 'belowMin') {
            text = (<>
              {strings.minAmountErrorPrefix}
              <Text NumbersThin-14 Red>
                £1
              </Text>
            </>);
        }
        else if (amountError === 'exceedBalance') {
            text = strings.exceedBalanceError;
        }
        return { isError, text };
    }, [amountError, remainingAllowance]);
    const onAmountChange = useCallback((_formatted, extracted = '') => {
        setAmountText(extracted);
        const value = parseMoneyInputText(extracted);
        if (value) {
            checkAmountError(value);
        }
        else {
            setAmountError(undefined);
        }
    }, [checkAmountError]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle, styles.contentContainer);
    return (<AppView withFrame={false}>
          <AnimatedKeyboardAwareScrollView enableOnAndroid onScroll={scrollHandler} scrollEventThrottle={16} extraHeight={extraHeightWihOnlyAmount} keyboardShouldPersistTaps="handled" contentContainerStyle={contentContainerStyle} showsVerticalScrollIndicator={false}>
            {From}
            <Spacer h={16}/>
            <View>
              {To}
              <View style={styles.iconContainer} pointerEvents="none">
                <View style={styles.iconWrapper}>
                  <IconBoldArrow alt colorKey="brand" direction="bottom"/>
                </View>
              </View>
            </View>
            <CardView noMarginBottom>
              <TouchableWithoutFeedback onPress={() => textInputRef.current?.focus()}>
                <View style={styles.amountContainer}>
                  <MoneyTextInput ref={textInputRef} value={amountText} onChangeText={onAmountChange} maxValue={1_000_000} prefix={getSymbolFromCurrency(defaultCurrency) ?? ''} style={styles.amountInput} placeholder={`${getSymbolFromCurrency(defaultCurrency) ?? ''}0`} selectionColor={colors.text.brand}/>
                  {!!footnote.text && (<Text TextThin-14 centered {...(footnote.isError ? { Red: true } : { Secondary: true })}>
                      {footnote.text}
                    </Text>)}
                </View>
              </TouchableWithoutFeedback>
            </CardView>
          </AnimatedKeyboardAwareScrollView>
          <KeyboardAvoidingButton brand title="Continue" onPress={handleContinue} disabled={!to || !from || !amount || !!amountError}/>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
    title: 'Deposit',
}));
const styleSet = createStyleSheets((colors) => ({
    iconContainer: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        top: -rem(30),
    },
    iconWrapper: {
        width: rem(42),
        height: rem(42),
        borderWidth: rem(1),
        alignItems: 'center',
        borderRadius: rem(44),
        justifyContent: 'center',
        borderColor: colors.borders.logoStroke,
        backgroundColor: colors.background.light,
    },
    amountContainer: {
        padding: rem(16),
        justifyContent: 'center',
    },
    amountInput: {
        ...styles.Numbers_24,
        color: colors.text.primary,
        alignSelf: 'center',
        textAlign: 'right',
        minWidth: rem(30),
        height: styles.Numbers_24.lineHeight,
        ...(isAndroid && { padding: 0 }),
    },
    contentContainer: {
        paddingTop: rem(16),
    },
}));
export default DepositToSecclUsingBankOrConnectedAccount;
